/**
 * useListing Hook
 *
 * Fetches listing data from Firestore.
 */

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';

export interface ListingPhoto {
  url: string;
  caption?: string;
}

export interface ListingHost {
  id: string;
  displayName: string;
  photoURL?: string;
  createdAt: string;
  responseRate?: number;
  responseTime?: string;
  isSuperhost?: boolean;
}

export interface ListingPricing {
  nightlyRate: number;
  weekendRate?: number;
  weeklyDiscount?: number;
  monthlyDiscount?: number;
  cleaningFee?: number;
  currency: string;
}

export interface ListingLocation {
  city: string;
  area: string;
  country: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface ListingHouseRules {
  checkInTime: string;
  checkOutTime: string;
  smokingAllowed: boolean;
  petsAllowed: boolean;
  partiesAllowed: boolean;
  additionalRules?: string[];
}

export interface ListingData {
  id: string;
  title: string;
  description: string;
  propertyType: string;
  photos: ListingPhoto[];
  location: ListingLocation;
  pricing: ListingPricing;
  host: ListingHost;
  bedrooms: number;
  beds: number;
  bathrooms: number;
  maxGuests: number;
  amenityIds: string[];
  houseRules: ListingHouseRules;
  cancellationPolicy: string;
  instantBooking: boolean;
  rating?: number;
  reviewCount?: number;
  minimumStay?: number;
}

interface UseListingReturn {
  listing: ListingData | null;
  isLoading: boolean;
  error: Error | null;
}

export function useListing(listingId: string): UseListingReturn {
  const [listing, setListing] = useState<ListingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchListing() {
      if (!listingId) {
        setError(new Error('No listing ID provided'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const docRef = doc(db, 'listings', listingId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          const listingData: ListingData = {
            id: docSnap.id,
            title: data.title || '',
            description: data.description || 'Beautiful property in Lebanon.',
            propertyType: data.propertyType || 'apartment',
            photos: data.photos || [],
            location: {
              city: data.location?.city || 'Lebanon',
              area: data.location?.state || 'Lebanon',
              country: data.location?.country || 'Lebanon',
              coordinates: data.location?.coordinates
                ? {
                    latitude: data.location.coordinates.lat,
                    longitude: data.location.coordinates.lng,
                  }
                : undefined,
            },
            pricing: {
              nightlyRate: data.pricing?.nightlyRate || 100,
              weekendRate: data.pricing?.weekendRate,
              cleaningFee: data.pricing?.cleaningFee,
              currency: data.pricing?.currency || 'USD',
            },
            host: {
              id: data.host?.id || data.hostId || 'host_1',
              displayName: data.host?.displayName || 'DARAK Host',
              photoURL: data.host?.photoURL,
              createdAt: data.host?.joinedDate || '2023-01-01',
              responseRate: data.host?.responseRate || 95,
              responseTime: data.host?.responseTime || 'within a few hours',
              isSuperhost: data.host?.isSuperhost || false,
            },
            bedrooms: data.capacity?.bedrooms || 1,
            beds: data.capacity?.beds || 1,
            bathrooms: data.capacity?.bathrooms || 1,
            maxGuests: data.capacity?.guests || 2,
            amenityIds: data.amenities || ['wifi', 'kitchen', 'parking'],
            houseRules: {
              checkInTime: data.checkInTime || '14:00',
              checkOutTime: data.checkOutTime || '12:00',
              smokingAllowed: false,
              petsAllowed: false,
              partiesAllowed: false,
            },
            cancellationPolicy: 'moderate',
            instantBooking: data.instantBook || false,
            rating: data.rating?.average,
            reviewCount: data.rating?.count,
            minimumStay: data.minNights || 1,
          };

          setListing(listingData);
        } else {
          setError(new Error('Listing not found'));
        }
      } catch (err) {
        console.error('Error fetching listing:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch listing'));
      } finally {
        setIsLoading(false);
      }
    }

    fetchListing();
  }, [listingId]);

  return { listing, isLoading, error };
}

export default useListing;
